import type { SupabaseClient } from '@supabase/supabase-js';
import { generateRecoveryCode } from './recoveryCode';
import type { AgeBand } from '../config/ages';
import { LEGAL_VERSION } from '../config/legal';

/**
 * Signup for a player who gives us no email address.
 *
 * The recovery code is generated HERE, in the browser, so it can be shown
 * immediately and so the server never has to hand one back. The server stores
 * only a bcrypt hash of it and cannot read it afterwards — which is the point,
 * and also why a lost code is a lost account.
 *
 * Everything else happens in the signup-noemail Edge Function, because a
 * synthetic address created from the browser would sit forever unconfirmed.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §5, §7.
 */

export type NoEmailResult = { ok: true; recoveryCode: string } | { ok: false; error: string };

function functionsUrl(): string | null {
  const base = import.meta.env.VITE_SUPABASE_URL;
  return base ? `${String(base).replace(/\/$/, '')}/functions/v1/signup-noemail` : null;
}

export async function registerNoEmail(
  client: SupabaseClient,
  username: string,
  password: string,
  ageBand: AgeBand,
  save?: unknown,
  captchaToken?: string,
): Promise<NoEmailResult> {
  const url = functionsUrl();
  if (!url) return { ok: false, error: 'Accounts are not available in this build' };

  const recoveryCode = generateRecoveryCode();

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // BOTH headers are required. Supabase verifies a JWT on Edge Functions
        // by default, and an apikey header alone is refused with 401 — which
        // local `functions serve --no-verify-jwt` hides. The anon key is a
        // valid JWT and is what an unauthenticated caller presents.
        apikey: String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''),
        Authorization: `Bearer ${String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '')}`,
      },
      body: JSON.stringify({
        username,
        password,
        recoveryCode,
        ageBand,
        termsVersion: LEGAL_VERSION,
        captchaToken,
        save,
      }),
    });
  } catch {
    return { ok: false, error: 'Could not reach the server. Check your connection.' };
  }

  let body: { ok?: boolean; error?: string; session?: unknown };
  try {
    body = (await response.json()) as typeof body;
  } catch {
    return { ok: false, error: 'Could not create that account' };
  }

  if (!response.ok || !body.ok) {
    return { ok: false, error: body.error ?? 'Could not create that account' };
  }

  // The function signs the new account in for us; adopt that session so the
  // player lands playing rather than at a login form.
  const session = body.session as
    | { access_token: string; refresh_token: string }
    | null
    | undefined;
  if (session?.access_token && session?.refresh_token) {
    await client.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
  }

  return { ok: true, recoveryCode };
}
